import isPast from "date-fns/isPast"
import parseISO from "date-fns/parseISO"
import { EnumLike } from "zod"

import { RuleValue, RuleValueUnits } from "@/v2/graphql/admin-generated-rq"

/**
 * The dash we use to indicate "no value".
 */
export const DASH = "—"

/**
 * The dash we use to indicate a negative number.
 */
export const MINUS = "–"

/**
 * Format a number of bytes as a string like "32 KiB"
 *
 * @param bytes: Byte count. Assumed to be non-negative.
 */
export function byteSize(bytes: number): string {
  if (bytes === 1.0) {
    return "1 byte"
  } else if (bytes < 1024) {
    return `${bytes} bytes`
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KiB`
  } else if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`
  } else {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`
  }
}

export type Formatter<T> = (input: T) => string
export type NumFormatter = Formatter<number>

// Identity formatter
export const id: Formatter<string> = (s: string) => s

// Shorthand for creating a new NumberFormat object.
const nf = (opts: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(undefined, opts).format

export const int: NumFormatter = nf({ maximumFractionDigits: 0 })

/** Format a number with x significant digits */
export const sig: (value: number) => NumFormatter = (value) => {
  return nf({
    minimumSignificantDigits: value,
    maximumSignificantDigits: value,
  })
}

/** Format a number with two significant digits */
export const sig2: NumFormatter = sig(2)

export const plainInt: NumFormatter = (n: number) => n.toFixed(0)

export const pct: NumFormatter = nf({
  style: "percent",
  minimumFractionDigits: 1,
})

export const smallPct: NumFormatter = nf({
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
})

export const tinyPct: NumFormatter = nf({
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const dp: (places: number) => NumFormatter =
  (places = 2) =>
  (num: number) => {
    return Intl.NumberFormat(undefined, {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    }).format(num)
  }

const _dollars: NumFormatter = nf({
  currency: "USD",
  style: "currency",
  maximumFractionDigits: 0,
})

export const dollars: NumFormatter = (num: number) => {
  if (Math.abs(num) < 1) {
    return "$0"
  }
  return _dollars(num)
}

export const shortCents: NumFormatter = nf({
  currency: "USD",
  style: "currency",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const cents: NumFormatter = nf({
  currency: "USD",
  style: "currency",
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

export const longCents: NumFormatter = nf({
  currency: "USD",
  style: "currency",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

export const shortDollars: NumFormatter = (num: number) => {
  if (Math.abs(num) < 1) {
    return "$0"
  }

  const pairs: [string, number][] = [
    ["B", 10 ** 9],
    ["M", 10 ** 6],
    ["K", 10 ** 3],
  ]
  const opts = { currency: "USD", style: "currency" }
  for (let [label, div] of pairs) {
    if (Math.abs(num) > div) {
      const fmt = nf({ ...opts, maximumSignificantDigits: 3 })
      // No decimals if we are in the thousands.
      const d = label == "K" ? Math.floor(num / div) : num / div
      return `${fmt(d)}${label}`
    }
  }

  return nf({ ...opts, maximumFractionDigits: 0 })(num)
}

export function usdPerKwh(num: number, decimalPlaces: number = 2): string {
  const cents = dp(decimalPlaces)(num)
  return `$${cents} / kWh`
}

/**
 * Truncate a `datetime.isoformat` style timestamp to just the day.
 */
export function date(isoformat: string): string {
  return isoformat.slice(0, 10)
}

const df = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(undefined, opts).format

const minuteLong = df({
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
})

const dayYearMonth = df({
  day: "numeric",
  year: "numeric",
  month: "long",
})

const numericDate = df({
  day: "numeric",
  month: "numeric",
  year: "numeric",
})

const numericMinute = df({
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
})

const numericMinuteTz = df({
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
})

/**
 * Format a possibly null number as a short USD
 */
export function usdShort(data: number | undefined | null): string {
  return F.opt(F.shortDollars, data)
}

/**
 * Format a `datetime.isoformat` style timestamp like "Tuesday, March 8, 2022
 * 10:46 PM" with one-minute precision.
 */
export function dtMinuteLong(isoformat: string): string {
  return minuteLong(parseISO(isoformat))
}

/**
 * Format a `datetime.isoformat` style timestamp with one-minute precision, like
 * "12/28/2022, 10:06 PM" in the U.S. locale
 */
export function dtNumericMinute(isoformat: string): string {
  return numericMinute(parseISO(isoformat))
}

/**
 * Format a `datetime.isoformat` style timestamp with one-minute precision, like
 * "12/28/2022, 10:06 PM GMT-8" in the U.S. locale
 */
export function dtNumericMinuteTz(isoformat: string): string {
  return numericMinuteTz(parseISO(isoformat))
}

/**
 * Format a `datetime` string to a "day year month" format eg
 * 21 July 2022
 */
export function dtDayYearMonth(dateString: string): string {
  return dayYearMonth(parseISO(dateString))
}

/**
 * Format a `datetime` string to a numeric date format eg
 * 01/01/2022
 */
export function dtNumericDate(dateString: string): string {
  return numericDate(parseISO(dateString))
}

/**
 * Format a `datetime` UTC string to a numeric date format as if it
 * were in the UTC timezone
 * e.g. "1970-01-01T00:00:00Z" => "01/01/1970", even if browser is GMT-8
 */
export function dtNumericDateUTC(dateString: string): string {
  return numericDate(dateToUTC(parseISO(dateString)))
}

/**
 * Format a `datetime` string according to format opts
 */
export function dtFormat(opts: Intl.DateTimeFormatOptions, dateString: string): string {
  const parsedISO = parseISO(dateString)
  const formatted = df(opts)(parsedISO)
  return formatted
}

/**
 * Given a `datetime` string return a boolean to express if the date is in the past
 */
export function isPastDate(dateString: string): boolean {
  return isPast(parseISO(dateString))
}

/**
 * Converts a `Date` object with timezone information to a `Date` object in UTC
 */
export function dateToUTC(date: Date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  )
}

/**
 * Apply a function and add a suffix to the formatted value.
 */
export function suf<T>(
  fmt: Formatter<T>,
  suffix: string,
  sep: string = " ",
): Formatter<T> {
  return (value: T) => `${fmt(value)}${sep}${suffix}`
}

/**
 * Apply a function and add a prefix to the formatted value.
 */
export function pre<T>(
  fmt: Formatter<T>,
  prefix: string,
  sep: string = " ",
): Formatter<T> {
  return (value: T) => `${prefix}${sep}${fmt(value)}`
}

/**
 * Apply a format function with a fallback in case of null.
 */
export function opt<T>(
  fmt: Formatter<T>,
  value: T | null | undefined,
  noneString = DASH,
): string {
  return value == null ? noneString : fmt(value)
}

export function bps(n: number): string {
  return `${Math.round(n * 10000)} bps`
}

export function ruleValue(ruleValue: RuleValue, units?: RuleValueUnits | null) {
  switch (ruleValue.__typename) {
    case "CurrencyValue":
      return cents(ruleValue.usd)
    case "YesNoValue":
      return ruleValue.yes ? "Yes" : "No"
    case "NumberValue":
      return ruleNumberValueWithUnits(ruleValue.number, units)
    case "IntegerValue":
      return ruleNumberValueWithUnits(ruleValue.integer, units)
    case "TextValue":
      return ruleValue.text
    default:
      return "Unknown format"
  }
}

function ruleNumberValueWithUnits(value: number, units?: RuleValueUnits | null) {
  switch (units) {
    case RuleValueUnits.Kw:
      return `${value} kW`
    case RuleValueUnits.Percentage:
      return pct(value)
    default:
      return String(value)
  }
}
export const numberWithCommaGrouping = nf({ useGrouping: true })

export function fmv(v: number): string {
  return `v${Math.floor(v / 100)}`
}

const shortDollarsRange = ({
  min,
  max,
}: {
  min?: number | null
  max?: number | null
}) => {
  const fmtMin = F.opt(F.shortDollars, min)
  const fmtMax = F.opt(F.shortDollars, max)

  if (min != null && max != null) {
    return fmtMin == fmtMax ? fmtMax : `${fmtMin} - ${fmtMax}`
  }
  if (min != null) return fmtMin
  if (max != null) return fmtMax
  return F.DASH
}

export function optEnum<T extends EnumLike = EnumLike>(
  enumLabels: Record<keyof T, string>,
  value: T[keyof T] | null | undefined,
  noneString = DASH,
) {
  if (!value) return noneString
  return enumLabels[value]
}

export const F = {
  DASH,
  MINUS,
  byteSize,
  id,
  int,
  sig,
  sig2,
  plainInt,
  pct,
  smallPct,
  tinyPct,
  dp,
  dollars,
  shortDollars,
  shortDollarsRange,
  shortCents,
  cents,
  longCents,
  usdPerKwh,
  usdShort,
  date,
  dtMinuteLong,
  dtNumericMinute,
  dtNumericMinuteTz,
  dtDayYearMonth,
  dtNumericDate,
  dtNumericDateUTC,
  dtFormat,
  isPastDate,
  dateToUTC,
  bps,
  opt,
  optEnum,
  suf,
  pre,
  fmv,
  ruleValue,
}
